import { useMemo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import C from "classnames";

// External Libraries
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { getSandboxLanguages } from "sj-ui-toolkit/src/helpers";
import { useMediaQuery, MEDIA_QUERY_BELOW_XL, useAnimationsEnabled } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { SandboxTab, SandboxLessonDetails } from "sj-ui-toolkit/src/client";

// Local context
import { useSandbox, useFilesystem, useSession } from "../contexts/SandboxHooks";
import { useTabs } from "../contexts/Tabs";
import { useFlagCelebration } from "./useFlagCelebration";

// Tab components
import Browser from "./Tabs/SandboxBrowser";
import Editor from "./Tabs/Editor";
import ProxyHistory from "./Tabs/ProxyHistory";
import Tests from "./Tabs/Tests";

import LeftPanel from "../LeftPanel/LeftPanel";
import OutputPanel from "../OutputPanel/OutputPanel";

// Dialog components
import SandboxStatusLoader from "./Dialogs/SandboxStatusLoader";
import SessionExpiredPage from "./Dialogs/SessionExpiredPage";
import SessionExpiringOverlay from "./Dialogs/SessionExpiringOverlay";
import SubmitFlagDialog from "./Dialogs/SubmitFlagDialog";

// Other local components
import ActionBar from "../../ActionBar/ActionBar";

// Module styles
import S from "../_styles.module.scss";

// Main component
interface SandboxControlsProps {
  tabs: SandboxTab[];
  engines: string[];
  isCtf: boolean;
  lessonDetails: SandboxLessonDetails;
}

// Not part of the svc-content SandboxTab union — folded in client-side only when stacked.
type DisplayTab = SandboxTab | { title: string; type: "instructions" | "output" };

const SandboxControls = ({ tabs, engines, isCtf, lessonDetails }: SandboxControlsProps) => {
  const { activeTabIndex, setActiveTab } = useTabs();
  const isStacked = useMediaQuery(MEDIA_QUERY_BELOW_XL);
  const displayTabs: DisplayTab[] = isStacked
    ? [
        { title: "Instructions", type: "instructions" },
        ...tabs,
        { title: "Output", type: "output" },
      ]
    : tabs;

  // Stacking prepends Instructions, so shift the index to keep the same tab selected.
  const wasStacked = useRef(isStacked);
  useLayoutEffect(() => {
    if (wasStacked.current === isStacked) {
      return;
    }
    wasStacked.current = isStacked;
    if (isStacked) {
      setActiveTab(activeTabIndex + 1);
    } else if (activeTabIndex === 0) {
      setActiveTab(0);
    } else {
      setActiveTab(Math.min(activeTabIndex - 1, tabs.length - 1));
    }
  }, [isStacked, activeTabIndex, setActiveTab, tabs.length]);

  const {
    currentLanguage,
    resetSandbox,
    hasEditor,
    hasTests,
    isTestRunning,
    runTests,
    testStatus,
    onLessonFinish,
    isSandboxLessonComplete,
    allStepsCompleted,
    correctFlagSubmitted,
  } = useSandbox();
  const { impersonatedEmail } = useUserContext();
  const isImpersonating = !!impersonatedEmail;

  const { editor, saveCurrentFile } = useFilesystem();
  const languageOptions = getSandboxLanguages(engines || []);

  // CTF "flag captured" celebration, behind an LD kill switch and the tenant's animations setting
  const { ctfFlagCelebration } = useFlags();
  const { animationsEnabled } = useAnimationsEnabled(getCurrentSelectedTenant());
  const celebrationEnabled = ctfFlagCelebration === true && isCtf && animationsEnabled;
  const flagCaptured = celebrationEnabled && correctFlagSubmitted;
  const headerRef = useRef<HTMLDivElement>(null);
  useFlagCelebration({
    enabled: celebrationEnabled,
    captured: correctFlagSubmitted,
    containerRef: headerRef,
  });

  // State for flag submission modal
  const [showFlagModal, setShowFlagModal] = useState(false);

  // Build actions & message for action bar
  const Actions = useMemo(() => {
    const actions = [
      {
        label: "Finish lesson",
        icon: "check",
        onClick: () => {
          if (onLessonFinish) {
            onLessonFinish();
          } else {
            console.warn("onLessonFinish callback not provided");
          }
        },
      },
    ];
    return actions;
  }, [testStatus, onLessonFinish]);

  const Message = useMemo(() => {
    if (testStatus?.status === "passed") {
      return "Congratulations, all tests have passed!";
    } else if (correctFlagSubmitted) {
      return "Congratulations, you submitted the correct flag!";
    } else if (allStepsCompleted) {
      return "Congratulations, all steps have been completed!";
    }
  }, [testStatus, correctFlagSubmitted, allStepsCompleted]);

  // Access timeout status. Override with URL param for testing.
  const [searchParams] = useSearchParams();
  const { session } = useSession();
  const timeoutStatus = searchParams.get("timeoutStatus") ?? session.timeout;

  // Browser ctrl/cmd key sequence
  const ctrlKeySeq = /Mac|Mac OS|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) ? "⌘" : "Ctrl+";

  // Determine button states
  const saveAndRunDisabled = useMemo(
    () => isTestRunning || (hasEditor && !editor.currentContents),
    [hasEditor, isTestRunning, editor.currentContents]
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (macOS)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault(); // Prevent browser's default save dialog

        // Only save if editor is dirty
        if (editor.isDirty) {
          saveCurrentFile();
        }
      }

      // Check for Ctrl+R (Windows/Linux) or Cmd+R (macOS)
      if ((e.ctrlKey || e.metaKey) && e.key === "r") {
        e.preventDefault(); // Prevent browser's default reload

        // Handle save and run
        if (!saveAndRunDisabled) {
          handleSaveAndRun();
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor.isDirty, saveAndRunDisabled, saveCurrentFile, runTests]);

  // Handle save and run tests
  const handleSaveAndRun = async () => {
    // Save file if editor is dirty
    if (hasEditor && editor.isDirty) {
      const saved = await saveCurrentFile();
      if (!saved) {
        console.error("Failed to save file before running tests");
        return;
      }
    }

    // Run tests
    try {
      // Kick off test run without awaiting results
      runTests();
      // Switch over to Tests tab
      const testsTabIndex = displayTabs.findIndex(tab => tab.type === "tests");
      if (testsTabIndex !== -1) {
        setActiveTab(testsTabIndex);
      }
    } catch (error) {
      console.error("Error running tests:", error);
    }
  };

  // Show session expired page if timed out
  if (timeoutStatus === "expired") {
    return <SessionExpiredPage />;
  }

  return (
    <div
      className={C(
        "flex flex-col h-full gap-2 relative",
        isStacked && [T.bgElevation1, "rounded-sm"]
      )}
      data-testid="sandbox-controls"
    >
      {/* Show a status loader while the sandbox is initializing */}
      <SandboxStatusLoader />

      {/* Session Expiring Warning Overlay */}
      <SessionExpiringOverlay />

      <div ref={headerRef} className={`flex gap-2 ${S.sandboxHeader}`}>
        {isCtf && (
          <Button
            icon={flagCaptured ? "pi pi-flag-fill" : "pi pi-flag"}
            severity={flagCaptured ? "success" : undefined}
            size="small"
            aria-label={flagCaptured ? "Flag captured" : "Submit flag"}
            aria-disabled={flagCaptured || undefined}
            data-testid="sandbox-submit-flag-button"
            onClick={() => {
              if (!flagCaptured) setShowFlagModal(true);
            }}
            disabled={isImpersonating}
            pt={flagCaptured ? { icon: { className: S.flagCapturedIcon } } : undefined}
          >
            <span className={C("ml-1", S.sandboxHeaderLabel)}>
              {flagCaptured ? "Flag captured" : "Submit flag"}
            </span>
          </Button>
        )}
        {hasTests && (
          <Button
            icon={hasEditor ? "pi pi-check" : "pi pi-play"}
            disabled={saveAndRunDisabled || isImpersonating}
            size="small"
            aria-label={hasEditor && editor.isDirty ? "Save code & run tests" : "Run tests"}
            data-testid="sandbox-run-tests-button"
            onClick={handleSaveAndRun}
          >
            <span className="ml-1">
              {hasEditor && editor.isDirty ? "Save code & run tests" : "Run tests"}
            </span>
            <span className={C("ml-1", S.sandboxHeaderLabel)}>{`${ctrlKeySeq}R`}</span>
          </Button>
        )}
        {hasEditor && (
          <Button
            icon="pi pi-save"
            text
            disabled={!editor.isDirty || isImpersonating}
            onClick={saveCurrentFile}
            size="small"
            aria-label="Save code"
            data-testid="sandbox-save-code-button"
          >
            <span className={C("ml-1", S.sandboxHeaderLabel)}>Save code</span>
            <span className={C("ml-1", S.sandboxHeaderLabel)}>{`${ctrlKeySeq}S`}</span>
          </Button>
        )}
        {languageOptions.length > 0 && (
          <Dropdown
            aria-label="Select sandbox language"
            value={currentLanguage}
            options={languageOptions}
            onChange={e => resetSandbox(e.value)}
            data-testid="sandbox-language-dropdown"
            pt={{
              input: { className: "body-sm" },
              item: { className: "body-sm" },
              trigger: { "aria-label": "Select sandbox language" },
              select: { "aria-label": "Select sandbox language" },
            }}
            disabled={isImpersonating}
          />
        )}
        <Button
          icon="pi pi-refresh"
          outlined
          severity="secondary"
          text
          size="small"
          onClick={() => resetSandbox(undefined)}
          className="ml-auto"
          label="Reset sandbox"
          aria-label="Reset sandbox"
          data-testid="sandbox-reset-button"
          disabled={isImpersonating}
          pt={{ label: { className: S.sandboxHeaderLabel } }}
        />
      </div>
      {celebrationEnabled && (
        <div
          role="status"
          aria-live="polite"
          className="sr-only"
          data-testid="sandbox-flag-captured-status"
        >
          {correctFlagSubmitted
            ? "Flag captured. Congratulations, you submitted the correct flag!"
            : ""}
        </div>
      )}
      <div className={S.sandboxContent}>
        <TabView
          aria-label="Sandbox environment"
          activeIndex={activeTabIndex}
          onTabChange={e => setActiveTab(e.index)}
          renderActiveOnly={false}
          className="h-full"
          data-testid="sandbox-tabs"
          pt={{
            root: { className: "h-full" },
            panelContainer: { className: S.tabContent },
          }}
        >
          {displayTabs.map(tab => (
            <TabPanel key={`${tab.type}-${tab.title}`} header={tab.title} contentClassName="h-full">
              {(() => {
                switch (tab.type) {
                  case "app":
                    return <Browser {...tab} />;
                  case "intercept":
                    return <ProxyHistory />;
                  case "editor":
                    return <Editor />;
                  case "tests":
                    return <Tests />;
                  case "instructions":
                    return <LeftPanel lessonDetails={lessonDetails} />;
                  case "output":
                    return <OutputPanel />;
                  default:
                    return null;
                }
              })()}
            </TabPanel>
          ))}
        </TabView>
      </div>
      {isSandboxLessonComplete && (
        <ActionBar
          actions={Actions}
          message={Message}
          className={celebrationEnabled ? S.actionBarSlideUp : undefined}
        />
      )}

      {/* Flag Submission Modal */}
      <SubmitFlagDialog visible={showFlagModal} onHide={() => setShowFlagModal(false)} />
    </div>
  );
};

export default SandboxControls;
